/**
 * Pipeline Health admin page.
 *
 * Surfaces TaskRunLog state for every monitored Celery task. The
 * `unattached_card` column on the store ledger is the symptom; this
 * page is the diagnosis — it shows whether the worker is running, how
 * recently each task fired, and how many items the last run recorded.
 *
 * Auto-refreshes every 60 seconds.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface RunSummary {
  started_at: string;
  status: string;
  items_recorded: number;
  items_processed: number;
  duration_ms: number | null;
}

interface TaskHealth {
  task_name: string;
  run_count_24h: number;
  success_count_24h: number;
  failure_count_24h: number;
  total_items_recorded_24h: number;
  last_run_at: string | null;
  last_success_at: string | null;
  minutes_since_last_run: number | null;
  minutes_since_last_recorded_item: number | null;
  consecutive_zero_runs: number;
  last_10_runs: RunSummary[];
  health: 'green' | 'amber' | 'red';
  health_reason: string;
}

interface PipelineHealthData {
  tasks: TaskHealth[];
  now_utc: string;
  is_business_hours: boolean;
}

interface RunDetail extends RunSummary {
  id: number;
  finished_at: string | null;
  items_skipped: number;
  items_errored: number;
  error_message: string;
  result_json: Record<string, unknown> | null;
}

const DOT: Record<string, string> = {
  green: '#16a34a',
  amber: '#f59e0b',
  red: '#dc2626',
};

const fmtMinutes = (m: number | null) => {
  if (m === null) return '—';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const PipelineHealth: React.FC = () => {
  const [data, setData] = useState<PipelineHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, RunDetail[]>>({});

  const load = useCallback(async () => {
    try {
      const res = await api.get<PipelineHealthData>('/admin/pipeline-health/');
      setData(res.data);
      setError(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (taskName: string) => {
    try {
      const res = await api.get<{ runs: RunDetail[] }>(
        `/admin/pipeline-health/runs/${taskName}/`,
      );
      setDetails((d) => ({ ...d, [taskName]: res.data.runs }));
    } catch (err) {
      // non-fatal — the summary still rendered
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading && !data) return <div style={{ padding: 24 }}>Loading…</div>;
  if (error && !data) {
    return <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>;
  }
  if (!data) return null;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
        Pipeline Health
      </h1>
      <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
        Auto-refreshes every 60 s · Business hours:{' '}
        {data.is_business_hours ? 'yes' : 'no'} · Now (UTC):{' '}
        {new Date(data.now_utc).toLocaleString()}
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {data.tasks.map((task) => {
          const isOpen = expanded === task.task_name;
          const maxBar = Math.max(
            1,
            ...task.last_10_runs.map((r) => r.items_recorded),
          );
          return (
            <div
              key={task.task_name}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 20,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: DOT[task.health] || '#9ca3af',
                    flexShrink: 0,
                  }}
                />
                <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 600 }}>
                  {task.task_name}
                </div>
                <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 13 }}>
                  {task.health_reason}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                  gap: 16,
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                <div>
                  <div style={{ color: '#6b7280' }}>Last ran</div>
                  <div style={{ fontWeight: 600 }}>
                    {fmtMinutes(task.minutes_since_last_run)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280' }}>Last item recorded</div>
                  <div style={{ fontWeight: 600 }}>
                    {fmtMinutes(task.minutes_since_last_recorded_item)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280' }}>Runs 24h</div>
                  <div style={{ fontWeight: 600 }}>
                    {task.run_count_24h} ({task.success_count_24h}✓ /{' '}
                    {task.failure_count_24h}✗)
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280' }}>Items 24h</div>
                  <div style={{ fontWeight: 600 }}>
                    {task.total_items_recorded_24h}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280' }}>Zero-run streak</div>
                  <div style={{ fontWeight: 600 }}>
                    {task.consecutive_zero_runs}
                  </div>
                </div>
              </div>

              {/* mini chart — bars of items_recorded for last 10 runs */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'end',
                  gap: 4,
                  height: 56,
                  marginBottom: 12,
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: 6,
                }}
              >
                {task.last_10_runs
                  .slice()
                  .reverse()
                  .map((r, i) => {
                    const h = Math.max(2, (r.items_recorded / maxBar) * 50);
                    const color =
                      r.status === 'success'
                        ? r.items_recorded > 0
                          ? '#16a34a'
                          : '#9ca3af'
                        : '#dc2626';
                    return (
                      <div
                        key={i}
                        title={`${new Date(r.started_at).toLocaleTimeString()} · ${
                          r.items_recorded
                        } items · ${r.status}`}
                        style={{
                          width: 18,
                          height: h,
                          background: color,
                          borderRadius: 2,
                        }}
                      />
                    );
                  })}
                {task.last_10_runs.length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>
                    No runs in window
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  if (isOpen) {
                    setExpanded(null);
                  } else {
                    setExpanded(task.task_name);
                    if (!details[task.task_name]) {
                      loadDetail(task.task_name);
                    }
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {isOpen ? 'Hide' : 'Show'} last 50 runs
              </button>

              {isOpen && (
                <div
                  style={{
                    marginTop: 12,
                    maxHeight: 400,
                    overflow: 'auto',
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                        <th style={{ padding: 6 }}>Started</th>
                        <th style={{ padding: 6 }}>Status</th>
                        <th style={{ padding: 6 }}>Recorded</th>
                        <th style={{ padding: 6 }}>Skipped</th>
                        <th style={{ padding: 6 }}>Errors</th>
                        <th style={{ padding: 6 }}>Duration</th>
                        <th style={{ padding: 6 }}>Error / Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details[task.task_name] || []).map((r) => (
                        <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: 6 }}>
                            {new Date(r.started_at).toLocaleString()}
                          </td>
                          <td style={{ padding: 6 }}>{r.status}</td>
                          <td style={{ padding: 6 }}>{r.items_recorded}</td>
                          <td style={{ padding: 6 }}>{r.items_skipped}</td>
                          <td style={{ padding: 6 }}>{r.items_errored}</td>
                          <td style={{ padding: 6 }}>
                            {r.duration_ms !== null ? `${r.duration_ms}ms` : '—'}
                          </td>
                          <td
                            style={{
                              padding: 6,
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={
                              r.error_message ||
                              JSON.stringify(r.result_json || {})
                            }
                          >
                            {r.error_message ||
                              JSON.stringify(r.result_json || {}).slice(0, 80)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PipelineHealth;
